/// <amd-module name="Test/TestPlansHubRefresh/TestCaseLinkedItems/Component/TestCaseLinkedSuitesConnectedComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import * as Resources from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Resources";
import * as Constants from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Constants";
import {
    ITestSuiteItem,
    ILinkedItemsAwareState,
} from "Test/TestPlansHubRefresh/TestCaseLinkedItems/Redux/TestCaseLinkedItemsContracts";
import { TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { ITfsPageService } from "Tfs/Platform/Page";
import { CustomCard } from "VSSUI/Card";
import { IVssPageContext } from "VSS/Platform/Context";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { IListSelection, ListSelection } from "VSSUI/List";
import { routeUrl } from "VSS/Platform/Location";
import { Components, IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ITableColumn, Table, renderSimpleCell, SimpleTableCell } from "VSSUI/Table";
import { ISimpleListCell, renderListCell } from "VSSUI/List";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface ITestCaseLinkedSuitesProps extends IVssComponentProperties {
    testSuiteList: ITestSuiteItem[];
}

/**
 * TestCaseLinkedSuitesConnectedComponent: Renders the test suites tab
 */
export class TestCaseLinkedSuitesConnectedComponent extends VssComponent<ITestCaseLinkedSuitesProps, IVssComponentContext> {
    private _items: ObservableArray<ITestSuiteItem>;
    private _tableColumns: ITableColumn<any>[];
    private _pageContext: IVssPageContext;
    private _selection: IListSelection;

    constructor(props: ITestCaseLinkedSuitesProps, context?: IVssComponentContext) {
        super(props, context);
        this._items = new ObservableArray<ITestSuiteItem>();
        this._tableColumns = this._getColumns();
        this._pageContext = context!.pageContext;
        this._selection = new ListSelection(true);
    }

    public shouldComponentUpdate(nextProps: ITestCaseLinkedSuitesProps) {
        if (nextProps.testSuiteList) {
            this._items.removeAll();
            for (let i = 0; i < nextProps.testSuiteList.length; i++) {
                this._items.push(nextProps.testSuiteList[i]);
            }
        }

        return true;
    }

    public render(): JSX.Element {
        let card = <Spinner className="spinner-card flex-grow" size={SpinnerSize.medium} />;
        if(this.props.testSuiteList) {
            card = (
                <CustomCard className="testplans-hub-linked-panel-card">
                    <Table
                        className="testplans-hub-linked-panel-table"
                        containerClassName="custom-scrollbar"
                        columns={this._tableColumns}
                        itemProvider={this._items}
                        showLines={true}
                        scrollable={true}
                        singleClickActivation={false}
                        onActivate={this._openTestSuite}
                        selection={this._selection}
                    />
                </CustomCard>
            );
        }
        
        return <div className="flex-grow">{card}</div>;
    }

    private _getColumns(): ITableColumn<any>[] {
        const tableColumns: ITableColumn<any>[] = [];
        tableColumns.push(
            {
                id: Constants.TitleColumnId,
                name: Resources.TitleColumnName,
                width: new ObservableValue(270),
                onSize: this._onColumnSize,
                renderCell: this._renderTitleColumn
            },
            {
                id: Constants.PlanColumnId,
                name: Resources.PlanColumnName,
                width: new ObservableValue(150),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.ProjectColumnId,
                name: Resources.ProjectColumnName,
                width: new ObservableValue(150),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            }
        );
        return tableColumns;
    }

    private _onColumnSize = (event: MouseEvent, columnIndex: number, width: number) => {
        const originalColumnWidth = this._tableColumns[columnIndex].width;
        (originalColumnWidth as ObservableValue<number>).value = width;
    };

    private _openTestSuite = (): void => {
        const testHubRoute: string = "ms.vss-test-web.testplans-hub-refresh-route";
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = tfsPageData && tfsPageData.project ? tfsPageData.project.name : "";

        const index = this._selection.value[0].beginIndex;
        routeValues["planId"] = String(this.props.testSuiteList[index].planId);
        routeValues["suiteId"] = String(this.props.testSuiteList[index].suiteId);
        const testSuiteUrl = routeUrl(this._pageContext, testHubRoute, routeValues);

        const openedWindow = window.open(testSuiteUrl, "_blank");
        if (openedWindow) {
            openedWindow.opener = null;
        }
    };

    private _renderTitleColumn(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<any>, tableItem: ITestSuiteItem): JSX.Element {
        let iconName;
        if (tableItem) {
            if (tableItem.suiteType === TestSuiteType.DynamicTestSuite) {
                iconName = "FolderQuery";
            } else if (tableItem.suiteType === TestSuiteType.StaticTestSuite) {
                iconName = "FolderHorizontal";
            } else {
                iconName = "FolderList";
            }
        }
        let simpleListCell: ISimpleListCell = {
            iconProps: {
                className: "test-suite-folder-icon",
                iconName: iconName
            },
            text: tableItem.title
        };
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                className="bolt-table-cell-content flex-row flex-center linked-items-table-cell"
            >
                {renderListCell(simpleListCell)}
            </SimpleTableCell>
        );
    }
}

function mapStateToProps(state: ILinkedItemsAwareState): Partial<ITestCaseLinkedSuitesProps> {
    let testSuiteList;
    if (state && state.linkedItemsState) {
        testSuiteList = state.linkedItemsState.testSuiteList;
    }
    return {
        testSuiteList: testSuiteList
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCaseLinkedSuitesProps> {
    return {
        dispatch
    };
}

export const TestCaseLinkedItemsSuitesComponent = connect(mapStateToProps, mapDispatchToProps)(TestCaseLinkedSuitesConnectedComponent);

Components.add("testcase-linked-items-suites-panel", TestCaseLinkedItemsSuitesComponent);
